/* global theme */

/*
  PSM Tariff Surcharge — self-hosted replacement for the Upcharge Fees app
  (the single "Tariff Surcharge (Korea)" rule: a % fee on a given vendor's
  line items, for visitors in a given country).

  Split of responsibilities:
  - THIS theme module decides WHETHER the fee line should exist: it checks the
    visitor country + whether the cart holds any line from the configured vendor,
    then adds/removes the fee product line (qty 1) carrying the marker property
    `_tariff: auto`. It also stamps the computed amount as `_tariff_amount` so the
    Function can honor it when server-side vendor data isn't available.
  - A Shopify Cart Transform Function sets the actual line PRICE (the one thing the
    AJAX cart can't do client-side). It only touches lines marked `_tariff: auto`,
    so it never disturbs the legacy Upcharge fee lines on the live theme.

  Config is rendered into window.theme.tariff from the fee product's `tariff`
  metafields (see layout/theme.liquid). If the product/config is absent, the
  feature is inert.
*/
(function () {
  'use strict';

  if (window.__psmTariffInit) return;
  window.__psmTariffInit = true;

  var cfg = (window.theme && window.theme.tariff) || null;
  var visitorCountry = ((window.theme && window.theme.visitorCountry) || '').toUpperCase();

  // When the Cart Transform Function recomputes the fee from vendor data server-side,
  // flip this to true and the module stops managing `_tariff_amount` (presence only).
  var FUNCTION_RECOMPUTES = false;

  var FEE_VARIANT_ID = cfg ? Number(cfg.variantId) : 0;
  var PCT = cfg ? (parseFloat(cfg.percent) || 0) : 0;
  var VENDOR = cfg ? String(cfg.vendor || '') : '';
  var TARGET_COUNTRY = cfg ? String(cfg.country || 'US').toUpperCase() : 'US';

  // `active` may arrive as a real boolean (boolean metafield) or the string "true".
  var ACTIVE = cfg ? (cfg.active === true || String(cfg.active).toLowerCase() === 'true') : false;

  function isEnabled() {
    return !!(ACTIVE && FEE_VARIANT_ID && VENDOR && PCT > 0);
  }

  var reconciling = false;
  var debounceTimer = null;

  function vendorSubtotalCents(cart) {
    var total = 0;
    cart.items.forEach(function (item) {
      if (item.variant_id === FEE_VARIANT_ID) return; // never tax the fee line itself
      if ((item.vendor || '') === VENDOR) {
        total += typeof item.final_line_price === 'number' ? item.final_line_price : item.line_price;
      }
    });
    return total;
  }

  function findFeeLine(cart) {
    for (var i = 0; i < cart.items.length; i += 1) {
      if (cart.items[i].variant_id === FEE_VARIANT_ID) return cart.items[i];
    }
    return null;
  }

  function eligible(cart) {
    if (!isEnabled()) return false;
    if (TARGET_COUNTRY && visitorCountry && visitorCountry !== TARGET_COUNTRY) return false;
    return vendorSubtotalCents(cart) > 0;
  }

  function getCart() {
    return fetch(theme.routes.cart + '.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  function feeProps(amountCents) {
    var props = { _tariff: 'auto' };
    if (!FUNCTION_RECOMPUTES) props._tariff_amount = (amountCents / 100).toFixed(2);
    return props;
  }

  function addFee(amountCents) {
    return fetch(theme.routes.cartAdd, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: FEE_VARIANT_ID, quantity: 1, properties: feeProps(amountCents) }] })
    });
  }

  function removeLine(lineKey) {
    return fetch(theme.routes.cartChange, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    });
  }

  function reconcile() {
    if (!isEnabled() || reconciling) return Promise.resolve();
    reconciling = true;

    return getCart()
      .then(function (cart) {
        var fee = findFeeLine(cart);
        var want = eligible(cart);

        if (!want) {
          return fee ? removeLine(fee.key).then(function () { return true; }) : false;
        }

        var amountCents = Math.round((vendorSubtotalCents(cart) * PCT) / 100);
        var desired = (amountCents / 100).toFixed(2);

        if (!fee) {
          return addFee(amountCents).then(function () { return true; });
        }
        // Fee already present. If we're managing the amount and it drifted, re-stamp it
        // (remove + re-add, since /cart/change.js can't reliably edit line properties).
        if (!FUNCTION_RECOMPUTES) {
          var current = fee.properties && fee.properties._tariff_amount;
          if (current !== desired) {
            return removeLine(fee.key)
              .then(function () { return addFee(amountCents); })
              .then(function () { return true; });
          }
        }
        return false;
      })
      .then(function (changed) {
        if (changed) document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
      })
      .catch(function (e) {
        if (window.console) console.warn('[psm-tariff] reconcile failed', e); // eslint-disable-line no-console
      })
      .finally(function () {
        reconciling = false;
      });
  }

  function schedule() {
    if (reconciling) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 250);
  }

  // React to cart mutations (quick-add, line qty change, drawer refresh).
  document.addEventListener('dispatch:cart-drawer:refresh', schedule);
  document.addEventListener('on:line-item:change', schedule);
  document.addEventListener('on:cart:add', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule);
  } else {
    schedule();
  }
})();
